import { differenceInCalendarDays, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { ET_TIMEZONE } from '@/components/live-sessions/constants'
import type {
  AcStatus,
  BulkConfirmRowStatus,
  EligibilityStatus,
  EnrollmentExpiryReason,
  TrialState,
} from '@/types/trial'

// Per-trial badge detail. The trial-status filter groups these into three
// coarser buckets (Active / Converted / Expired; see TrialStatusBucket in
// trialFilters.ts), but the badge keeps the finer wire-state distinction so
// e.g. a terminated trial still reads differently from one that simply
// expired.
export const TRIAL_STATE_LABELS: Record<TrialState, string> = {
  active: 'Active',
  // 'AC met' would duplicate the AC status badge wherever both render, so the
  // state label says what the EA can do next instead.
  ac_met: 'Ready to confirm',
  converted: 'Converted',
  expired_no_ac: 'Expired (no AC)',
  expired_ineligible: 'Expired (ineligible)',
  expired_ac_met: 'Expired (AC met)',
  terminated: 'Terminated',
}

export const ELIGIBILITY_STATUS_LABELS: Record<EligibilityStatus, string> = {
  pending: 'Pending review',
  confirmed: 'Eligibility confirmed',
  ineligible: 'Ineligible',
}

// Confirmed by org policy, not by an EA. Kept out of ELIGIBILITY_STATUS_LABELS
// because it is not a status value: it reads off a confirmed trial with no
// confirming EA.
export const BYPASSED_ELIGIBILITY_LABEL = 'Eligibility bypassed'

export const AC_STATUS_LABELS: Record<AcStatus, string> = {
  met: 'AC met',
  unmet: 'AC not met',
}

export const BULK_CONFIRM_ROW_STATUS_LABELS: Record<BulkConfirmRowStatus, string> = {
  converted: 'Converted',
  eligibility_set_ac_pending: 'Confirmed, awaiting AC',
  idempotent_noop: 'Already confirmed',
  error: 'Failed',
}

// Wire reason codes from the bulk confirm endpoint (ENG-2688 contract plus
// the internal precondition codes that pass through untranslated).
const BULK_CONFIRM_REASON_LABELS: Record<string, string> = {
  not_found: 'Trial not found',
  cross_org_destination: 'Destination group is in a different organization',
  conflict_different_group: 'Already confirmed into a different group',
  destination_group_not_found: 'Destination group not found',
  destination_group_deactivated: 'Destination group is deactivated',
  destination_group_is_trial: 'Destination group is a trial group',
  invalid_trial_state: 'Trial is not in a confirmable state',
  internal_error: 'Internal error, retry the batch',
}

// The backend treats reason as an open set, so unknown codes fall back to a
// humanized version of the raw code instead of disappearing.
export function formatBulkConfirmReason(reason: string | null): string {
  if (!reason) return 'Unknown error'
  const label = BULK_CONFIRM_REASON_LABELS[reason]
  if (label) return label
  const text = reason.replaceAll('_', ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export const EXPIRY_REASON_LABELS: Record<EnrollmentExpiryReason, string> = {
  trial_no_ac: 'Trial ended without meeting activation criteria',
  trial_ineligible: 'Trial ended marked ineligible',
  trial_unconverted: 'Trial ended with AC met but never converted',
  license_expired: 'License expired',
  manual_terminate: 'Manually terminated',
}

const TERMINAL_TRIAL_STATES: ReadonlySet<TrialState> = new Set([
  'converted',
  'expired_no_ac',
  'expired_ineligible',
  'expired_ac_met',
  'terminated',
])

export function isTerminalTrialState(state: TrialState): boolean {
  return TERMINAL_TRIAL_STATES.has(state)
}

// Mirrors _CONFIRMABLE_STATES in the backend's TrialStateValidator. Listed
// directly rather than derived from isTerminalTrialState, so both sides read as
// the same list and a drift shows up as a diff rather than as a button that
// 409s. Terminal is not the same as finished: an expired_ac_met trial met AC,
// so a late confirm still converts it.
const CONFIRMABLE_TRIAL_STATES: ReadonlySet<TrialState> = new Set(['active', 'ac_met', 'expired_ac_met'])

export function isConfirmableTrialState(state: TrialState): boolean {
  return CONFIRMABLE_TRIAL_STATES.has(state)
}

// Mirrors _MARK_INELIGIBLE_STATES in the backend's TrialEligibilityService.
// Narrower than confirmable on purpose: once a trial has expired, refusing it
// changes nothing the learner sees, so the backend rejects it with a 409.
const MARK_INELIGIBLE_TRIAL_STATES: ReadonlySet<TrialState> = new Set(['active', 'ac_met'])

export function isMarkIneligibleTrialState(state: TrialState): boolean {
  return MARK_INELIGIBLE_TRIAL_STATES.has(state)
}

// The eligibility badge answers "does this trial still need an EA verdict?".
// When a trial ends while eligibility is still pending (it expired without ever
// meeting AC, or was otherwise closed), confirming eligibility can no longer
// change anything, so a lingering amber "Pending" wrongly implies work remains.
// In that case the badge should read as expired instead.
export function isEligibilityExpired(eligibilityStatus: EligibilityStatus, trialState: TrialState): boolean {
  return eligibilityStatus === 'pending' && !isConfirmableTrialState(trialState)
}

// Shared so both eligibility badges (queue table and detail/user views) read the
// expired label from one source even though they use different palettes.
export const EXPIRED_ELIGIBILITY_LABEL = 'Expired'

export function formatLearnerName(learner: {
  readonly first_name: string | null
  readonly last_name: string | null
  readonly email: string
}): string {
  const name = [learner.first_name, learner.last_name].filter(Boolean).join(' ').trim()
  return name || learner.email
}

// 'ea' | 'system' | 'hubspot' | 'job' | 'simulator', with a safe fallback for
// any actor kind the backend adds later.
export function formatTrialActor(actorKind: string): string {
  switch (actorKind) {
    case 'ea':
      return 'EA'
    case 'hubspot':
      return 'HubSpot'
    case 'system':
      return 'System'
    case 'job':
      return 'Scheduled job'
    case 'simulator':
      return 'Simulator'
    default:
      return actorKind.charAt(0).toUpperCase() + actorKind.slice(1)
  }
}

// Calendar days from today until the trial's end date. Negative when the end
// date is in the past.
export function trialDaysRemaining(endDate: string, today: Date = new Date()): number {
  return differenceInCalendarDays(parseISO(endDate), today)
}

// Share of the trial window already elapsed, clamped to 0-100.
export function trialProgressPercent(startDate: string, endDate: string, today: Date = new Date()): number {
  const total = differenceInCalendarDays(parseISO(endDate), parseISO(startDate))
  if (total <= 0) return 100
  const elapsed = differenceInCalendarDays(today, parseISO(startDate))
  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)))
}

export function formatDaysRemaining(daysRemaining: number): string {
  if (daysRemaining > 1) return `${daysRemaining} days remaining`
  if (daysRemaining === 1) return '1 day remaining'
  if (daysRemaining === 0) return 'Ends today'
  return 'Ended'
}

// The two helpers below are the single place that knows this: converting a trial
// overwrites its enrollment's end_date with the extended program end date, so for
// a converted trial end_date (and the days_remaining derived from it) describes
// the program, not the trial. The trial's window closed when it converted.
// Everything that reports a trial window must go through here, or the queue row
// and the drawer it opens will disagree about the same trial (ENG-3199).

// Trial fields needed to resolve a trial window, satisfied by both
// TrialQueueItem and TrialDetailResponse.
export type TrialWindowFields = {
  readonly trial_state: TrialState
  readonly end_date: string
  readonly converted_at: string | null
}

// Trial milestones (converted_at, ac_met_at, eligibility_confirmed_at) are
// instants, whereas every trial date derived server-side is an Eastern-Time
// business date: start_date, end_date, and the ET "today" behind days_remaining
// (get_today_est). Rendering an instant as the viewer's calendar day would make
// one trial report different days depending on who is looking, and any
// evening-ET milestone would show as the day before. So resolve milestones to
// their ET calendar day and keep every trial date in one timezone.
//
// This is about which DAY a milestone belongs to. To show the precise moment,
// use formatDateTimeWithTimezone, which names the timezone it renders in.
function parseInstant(instant: string): Date {
  // A naive timestamp from the backend is conceptually UTC; parseISO would read
  // it as browser-local. Same guard as formatDateTimeWithTimezone.
  const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(instant)
  return parseISO(hasOffset ? instant : `${instant}Z`)
}

function easternCalendarDay(instant: string): string {
  return formatInTimeZone(parseInstant(instant), ET_TIMEZONE, 'yyyy-MM-dd')
}

// The ET calendar day an instant falls on, formatted for display.
export function formatEasternShortDate(instant: string): string {
  return formatInTimeZone(parseInstant(instant), ET_TIMEZONE, 'MMM d, yyyy')
}

// The date the trial window closed (or closes), as an ISO date. Null only for a
// converted trial with no recorded conversion date, which the caller must render
// as unknown rather than falling back to the program end date.
export function trialWindowEndDate(trial: TrialWindowFields): string | null {
  if (trial.trial_state !== 'converted') return trial.end_date
  return trial.converted_at === null ? null : easternCalendarDay(trial.converted_at)
}

// A countdown only means something while the window is open, so a converted trial
// has no days left to report: its days_remaining counts down the program.
export function trialWindowDaysRemaining(trial: {
  readonly trial_state: TrialState
  readonly days_remaining: number
}): number | null {
  return trial.trial_state === 'converted' ? null : trial.days_remaining
}

export function formatTrialDaysLeft(trial: {
  readonly trial_state: TrialState
  readonly days_remaining: number
}): string | null {
  const days = trialWindowDaysRemaining(trial)
  return days === null ? null : String(days)
}
